import { apiService } from './api'
import { z } from 'zod'
import { BOB_ENDPOINTS, SCAN_ENDPOINTS } from '../../api/endpoints'

const UserSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayName: z.string().nullable().optional(),
  email: z.string().optional(),
})

const TimeFrameSchema = z.object({
  preset: z.string(),
  from: z
    .string()
    .nullable()
    .optional()
    .transform(val => (val ? new Date(val) : undefined)),
  to: z
    .string()
    .nullable()
    .optional()
    .transform(val => (val ? new Date(val) : undefined)),
})

const MinimalScanSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  createdAt: z.string().nullable(),
  timeFrame: TimeFrameSchema.optional(),
  keywords: z.array(z.string()).optional(),
  location: z.string().optional(),
  createdBy: z.string().optional(),
})

const BobAnalyseResponseSchema = z.object({
  data: z.object({
    id: z.string(),
  }),
})

const BobAnalyseSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  beeldvorming: z.record(z.string(), z.array(z.string())).nullable(),
  oordeelsvorming: z.record(z.string(), z.array(z.string())).nullable(),
  besluitvorming: z.record(z.string(), z.array(z.string())).nullable(),
  status: z.string(),
  createdAt: z.string(),
  crisisId: z.string(),
  scanId: z.string().nullable().optional(),
  jobId: z.string().nullable(),
  createdBy: UserSchema.optional(),
  scan: MinimalScanSchema.optional(),
  customInput: z.string().nullable().optional(),
})

const BobAnalyseListResponseSchema = z.object({
  data: z.array(BobAnalyseSchema),
})

const BobAnalyseDetailResponseSchema = z.object({
  data: BobAnalyseSchema,
})

const AvailableScansResponseSchema = z.object({
  data: z.array(MinimalScanSchema),
})

// Suggestion schemas
const SuggestionSourceSchema = z.object({
  type: z.enum(['scan', 'custom_input']),
  scanId: z.string().optional(),
  scanTitle: z.string().optional(),
  label: z.string().optional(),
})

const BobSuggestionSchema = z.object({
  id: z.string(),
  bobAnalyseId: z.string(),
  sourceInputId: z.string().nullable().optional(),
  beeldvormingSuggestions: z.record(z.string(), z.array(z.string())).nullable().optional(),
  oordeelsvormingSuggestions: z.record(z.string(), z.array(z.string())).nullable().optional(),
  besluitvormingSuggestions: z.record(z.string(), z.array(z.string())).nullable().optional(),
  status: z.enum(['pending', 'processing', 'ready', 'merged', 'rejected', 'failed']),
  jobId: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  processedAt: z.string().nullable().optional(),
  mergedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  source: SuggestionSourceSchema.optional(),
})

const BobSuggestionsListResponseSchema = z.object({
  data: z.array(BobSuggestionSchema),
})

const AddSourceResultSchema = z.object({
  type: z.enum(['scan', 'custom_input']),
  sourceId: z.string().optional(),
  label: z.string().optional(),
  suggestionId: z.string().optional(),
  jobId: z.string().optional(),
  status: z.enum(['processing', 'duplicate']),
  message: z.string().optional(),
})

const AddSourceResponseSchema = z.object({
  data: z.object({
    results: z.array(AddSourceResultSchema),
  }),
})

// Source history schemas
const SourceHistoryItemSchema = z.object({
  id: z.string(),
  type: z.enum(['scan', 'custom_input']),
  createdAt: z.string(),
  processedAt: z.string().nullable().optional(),
  scan: z.object({
    id: z.string(),
    title: z.string(),
  }).optional(),
  label: z.string().optional(),
})

const SourceHistoryResponseSchema = z.object({
  data: z.array(SourceHistoryItemSchema),
})

export type BobAnalyse = z.infer<typeof BobAnalyseSchema>
export type BobAnalyseFormData = {
  scanId?: string
  beeldvorming?: Record<string, string[]> | null
  oordeelsvorming?: Record<string, string[]> | null
  besluitvorming?: Record<string, string[]> | null
}
export type AvailableScan = z.infer<typeof MinimalScanSchema>
export type BobSuggestion = z.infer<typeof BobSuggestionSchema>
export type AddSourceResult = z.infer<typeof AddSourceResultSchema>
export type SourceHistoryItem = z.infer<typeof SourceHistoryItemSchema>

export interface AddSourceInput {
  scanId?: string
  customInput?: {
    label: string
    text: string
  }
}

export interface MergeSectionsInput {
  sections?: {
    beeldvorming?: Record<string, string[]>
    oordeelsvorming?: Record<string, string[]>
    besluitvorming?: Record<string, string[]>
  }
}

export interface RejectSectionsInput {
  sections?: {
    beeldvorming?: string[]
    oordeelsvorming?: string[]
    besluitvorming?: string[]
  }
}

export class BobAnalyseService {
  /**
   * Create a new BOB analyse
   */
  async createBobAnalyse(data: BobAnalyseFormData) {
    const response = await apiService.post(BOB_ENDPOINTS.NEW, data)
    const parsed = BobAnalyseResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * List all BOB analyses for a specified crisis
   */
  async listBobAnalyses(crisisId?: string) {
    const params = crisisId ? { crisis_id: crisisId } : {}
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${BOB_ENDPOINTS.LIST}?${queryString}` : BOB_ENDPOINTS.LIST

    const response = await apiService.get(url)
    const parsed = BobAnalyseListResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Get a BOB analyse by ID
   */
  async getBobAnalyse(bobId: string) {
    const response = await apiService.get(BOB_ENDPOINTS.BOB_BY_ID(bobId))
    const parsed = BobAnalyseDetailResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Delete a BOB analyse
   */
  async deleteBobAnalyse(bobId: string) {
    await apiService.delete(BOB_ENDPOINTS.DELETE(bobId))
    return true
  }

  /**
   * Update a BOB analyse section content
   * @param bobId The ID of the BOB analyse
   * @param section The section to update ('beeldvorming' or 'oordeelsvorming' or 'besluitvorming')
   * @param category The category within the section
   * @param values The new values for the category, or null to delete the category
   */
  async updateBobAnalyseContent(
    bobId: string,
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string,
    values: string[] | null
  ) {
    const updateData = {
      [section]: {
        [category]: values,
      },
    }

    const response = await apiService.patch(BOB_ENDPOINTS.UPDATE(bobId), updateData)
    const parsed = BobAnalyseDetailResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Add a new category to a BOB analyse section
   * @param bobId The ID of the BOB analyse
   * @param section The section to add the category to
   * @param categoryName The name of the new category
   */
  async addCategory(
    bobId: string,
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    categoryName: string
  ) {
    // Initialize with empty array
    return this.updateBobAnalyseContent(bobId, section, categoryName, [])
  }

  /**
   * Delete a category from a BOB analyse section
   * @param bobId The ID of the BOB analyse
   * @param section The section containing the category
   * @param _currentContent The current section content (unused)
   * @param categoryName The name of the category to delete
   */
  async deleteCategory(
    bobId: string,
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    _currentContent: Record<string, string[]>,
    categoryName: string
  ) {
    console.log('[BobAnalyseService] Deleting category:', {
      bobId,
      section,
      categoryName,
    })

    return this.updateBobAnalyseContent(bobId, section, categoryName, null)
  }

  /**
   * Get all completed scans available for BOB analyse generation
   */
  async getAvailableScans(crisisId?: string) {
    const params = crisisId ? { crisis_id: crisisId } : {}
    const queryString = new URLSearchParams(params).toString()
    const url = queryString
      ? `${SCAN_ENDPOINTS.AVAILABLE}?${queryString}`
      : SCAN_ENDPOINTS.AVAILABLE

    const response = await apiService.get(url)
    const parsed = AvailableScansResponseSchema.parse(response)
    return parsed.data
  }

  // ============ Suggestion Methods ============

  /**
   * Add a source (scan or custom input) to a BOB analyse and generate suggestions
   */
  async addSource(bobId: string, input: AddSourceInput): Promise<AddSourceResult[]> {
    const response = await apiService.post(BOB_ENDPOINTS.ADD_SOURCE(bobId), input)
    const parsed = AddSourceResponseSchema.parse(response)
    return parsed.data.results
  }

  /**
   * Get all suggestions for a BOB analyse
   */
  async getSuggestions(bobId: string, status?: string): Promise<BobSuggestion[]> {
    const params = status ? { status } : {}
    const queryString = new URLSearchParams(params).toString()
    const url = queryString
      ? `${BOB_ENDPOINTS.SUGGESTIONS(bobId)}?${queryString}`
      : BOB_ENDPOINTS.SUGGESTIONS(bobId)

    const response = await apiService.get(url)
    const parsed = BobSuggestionsListResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Merge a suggestion into the BOB analyse
   */
  async mergeSuggestion(
    bobId: string,
    suggestionId: string,
    input?: MergeSectionsInput
  ): Promise<BobAnalyse> {
    const response = await apiService.post(BOB_ENDPOINTS.MERGE_SUGGESTION(bobId, suggestionId), input || {})
    const parsed = BobAnalyseDetailResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Reject a suggestion (fully or partially)
   */
  async rejectSuggestion(
    bobId: string,
    suggestionId: string,
    input?: RejectSectionsInput
  ): Promise<boolean> {
    await apiService.post(BOB_ENDPOINTS.REJECT_SUGGESTION(bobId, suggestionId), input || {})
    return true
  }

  /**
   * Get available scans that haven't been processed for this BOB yet
   */
  async getAvailableScansForBob(bobId: string): Promise<AvailableScan[]> {
    const response = await apiService.get(BOB_ENDPOINTS.AVAILABLE_SCANS(bobId))
    const parsed = AvailableScansResponseSchema.parse(response)
    return parsed.data
  }

  /**
   * Get the history of sources (scans and custom inputs) added to a BOB analyse
   */
  async getSourceHistory(bobId: string): Promise<SourceHistoryItem[]> {
    const response = await apiService.get(BOB_ENDPOINTS.SOURCE_HISTORY(bobId))
    const parsed = SourceHistoryResponseSchema.parse(response)
    return parsed.data
  }
}

// Create singleton instance
export const bobAnalyseService = new BobAnalyseService()
